import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { TenantWidgetAccessCreateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { TenantCreate } from "../../../tenant/components/create/TenantCreate";
import { TenantCreateStore } from "../../../tenant/store";
import { InputBoolean } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantWidgetAccessCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  tenantSelectedValue?: { displayId: string; id: string } | null;
  onTenantSelected?: (value: { displayId: string; id: string } | null) => void;
  onTenantChange?: (value: string) => void;
  tenantOptions?: { displayId: string; id: string }[];
  tenantError?: string;
  widgetSelectedValue?: { displayId: string; id: string } | null;
  onWidgetSelected?: (value: { displayId: string; id: string } | null) => void;
  onWidgetChange?: (value: string) => void;
  widgetOptions?: { displayId: string; id: string }[];
  widgetError?: string;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabled: boolean;
  enabledError?: string;
  configOverrideSelectedValue?: { displayId: string; id: string } | null;
  onConfigOverrideChange?: (value: string) => void;
  configOverride: string;
  configOverrideError?: string;
  tenantWidgetAccessCreateStore: TenantWidgetAccessCreateStore;
};

export const TenantWidgetAccessCreateForm: React.FC<TenantWidgetAccessCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onTenantSelected = () => {},
      tenantSelectedValue = null,
      tenantOptions = [],
      tenantError,
      onTenantChange = () => {},
      onWidgetSelected = () => {},
      widgetSelectedValue = null,
      widgetOptions = [],
      widgetError,
      onWidgetChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      configOverride = "{}",
      configOverrideError,
      onConfigOverrideChange = () => {},
      tenantWidgetAccessCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="TenantWidgetAccess"
              mode="create"
              values={{}}
              options={{ tenant: tenantOptions, widget: widgetOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "tenant":
                    onTenantSelected?.(valor);
                    break;
                  case "widget":
                    onWidgetSelected?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                  case "configOverride":
                    onConfigOverrideChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={tenantWidgetAccessCreateStore.getCardErrors("null")}
              title={langStore.groupTenantWidgetAccessLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="tenant"
                  label={langStore.TenantWidgetAccessTenantLabel}
                  autoFocus
                  error={Boolean(tenantError)}
                  helperText={
                    tenantError ||
                    tenantWidgetAccessCreateStore.warnings["tenant"]?.message
                  }
                  selectedValue={tenantSelectedValue}
                  onChange={(value: string) => onTenantChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onTenantSelected(value)
                  }
                  options={tenantOptions}
                  description={langStore.TenantWidgetAccessTenantDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "tenant_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new TenantCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <TenantCreate
                          tenantCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewTenant"
                      cancelMethod="cancelNewTenant"
                    />
                  )}
                />
                <InputReference
                  id="widget"
                  label={langStore.TenantWidgetAccessWidgetLabel}
                  error={Boolean(widgetError)}
                  helperText={
                    widgetError ||
                    tenantWidgetAccessCreateStore.warnings["widget"]?.message
                  }
                  selectedValue={widgetSelectedValue}
                  onChange={(value: string) => onWidgetChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onWidgetSelected(value)
                  }
                  options={widgetOptions}
                  description={langStore.TenantWidgetAccessWidgetDescription}
                  canCreate={false}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    tenantWidgetAccessCreateStore.warnings["enabled"]?.message
                  }
                  label={langStore.TenantWidgetAccessEnabledLabel}
                  description={langStore.TenantWidgetAccessEnabledDescription}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />

                <InputCodeEditor
                  value={configOverride}
                  error={Boolean(configOverrideError)}
                  helperText={
                    configOverrideError ||
                    tenantWidgetAccessCreateStore.warnings["configOverride"]
                      ?.message
                  }
                  label={langStore.TenantWidgetAccessConfigOverrideLabel}
                  onChange={(value: string) => onConfigOverrideChange(value)}
                  language="json"
                  description={
                    langStore.TenantWidgetAccessConfigOverrideDescription
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
